import { useFormContext, FieldPath, RegisterOptions, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInput, FormInputProps } from '@/components/forms/FormInput';
import { getFormError, getServerError } from '@/core/errors/FormError';

import { NO_XSS_REGEX } from '@/core/consts/regular-expressions/common';

export type ValidatedFormInputProps<T extends FieldValues = FieldValues> = Omit<FormInputProps, 'name' | 'errorInline' | 'errorSubmittion'> & {
    name: FieldPath<T>;
    additionalValidation?: RegisterOptions<T>;
    disableXSSValidation?: boolean;
};

export const FormInputWrapper = <T extends FieldValues = FieldValues>({
    name,
    additionalValidation = {},
    disableXSSValidation = false,
    ...props
}: ValidatedFormInputProps<T>) => {
    const { t } = useTranslation('');
    const { register, formState: { errors } } = useFormContext<T>();

    const createValidationRules = (additionalRules: RegisterOptions<T> = {}): RegisterOptions<T> => {
        const baseRules: RegisterOptions<T> = {};

        if (!disableXSSValidation) {
            baseRules.pattern = {
                value: NO_XSS_REGEX,
                message: t('common.invalid-format-xss')
            };
        }

        return {
            ...baseRules,
            ...additionalRules
        } as RegisterOptions
    };

    const getNestedError = (errors: any, path: string) => {
        return path.split('.').reduce((current, key) => current?.[key], errors);
    };

    const fieldError = getNestedError(errors, name);

    return (
        <FormInput
            {...register(name, createValidationRules(additionalValidation))}
            errorInline={getFormError(fieldError)}
            errorSubmittion={getServerError(fieldError)}
            {...props}
        />
    );
};

FormInputWrapper.displayName = 'FormInputWrapper';
